/**
 * Precios que quedaron bajo el margen.
 *
 * The margin floor used to guard only a price typed at the till. A purchase that raises what a
 * product costs moves every list price that sat near the floor below it, and from then on every
 * sale of it loses money, with nothing on any screen to say so. These are those prices, each with
 * the one that brings it back up to the floor.
 */

import {
  centavos,
  costoDeUnidadVenta,
  margenBp,
  precioParaMargen,
  type Centavos,
} from '@abastece/shared';
import type { Compra, Existencia, Precio, Producto, UnidadVenta } from '@abastece/core';
import { costoConocido } from './costos.js';

export interface PrecioBajo {
  productoId: string;
  unidadVentaId: string;
  listaId: string;
  precio: Centavos;
  /** What one sale unit costs. */
  costo: Centavos;
  margenBp: number;
  /** The floor's price, rounded up to the next fifty centavos: never under it. */
  sugerido: Centavos;
}

/** Up to the next C$ 0.50: a price somebody can charge in coins, and never below the floor. */
const hastaCincuenta = (c: Centavos): Centavos => centavos(Math.ceil(c / 50) * 50);

export function preciosBajoMargen(d: {
  productos: readonly Producto[];
  unidades: readonly UnidadVenta[];
  precios: readonly Precio[];
  existencias: readonly Existencia[];
  compras: readonly Compra[];
  minimoBp: number;
  /** Only these products, when given: the ones a purchase just touched. */
  soloProductos?: ReadonlySet<string>;
}): PrecioBajo[] {
  const activos = new Set(d.productos.filter((p) => p.activo).map((p) => p.id));
  const unidades = new Map(d.unidades.map((u) => [u.id, u]));
  const bajos: PrecioBajo[] = [];
  for (const p of d.precios) {
    const unidad = unidades.get(p.unidadVentaId);
    if (!unidad || !activos.has(unidad.productoId)) continue;
    if (d.soloProductos && !d.soloProductos.has(unidad.productoId)) continue;
    // What a unit costs where the counter sells it, or wherever else it is known.
    const costo = costoDeUnidadVenta(costoConocido(unidad.productoId, 'tienda', d.existencias, d.compras), unidad.factor);
    if (costo <= 0 || p.precio <= 0) continue;
    const margen = margenBp(p.precio, costo);
    if (margen >= d.minimoBp) continue;
    bajos.push({
      productoId: unidad.productoId,
      unidadVentaId: unidad.id,
      listaId: p.listaId,
      precio: p.precio,
      costo,
      margenBp: margen,
      sugerido: hastaCincuenta(precioParaMargen(costo, d.minimoBp)),
    });
  }
  return bajos.sort((a, b) => a.margenBp - b.margenBp);
}
